import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmt = (n) => Number(n).toLocaleString("en-US");
const fmtCoin = (n) => Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const INFO_OPTS = ["All", "Deposit", "Deposit (PGA)", "Withdraw", "Withdraw (PGA)", "Transaksi Manual"];

export default function HistoryKoin() {
  const { user } = useAuth();
  const [data, setData] = useState({ rows: [], page: 1, total_pages: 1, total: 0 });
  const [staffList, setStaffList] = useState([]);
  const [filters, setFilters] = useState({ info: "All", to: "", by: "All", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [staffLabel, setStaffLabel] = useState(user.name);

  const isTrainer = user.role === "admin" || user.role === "trainer";

  const load = useCallback((p = page) => {
    const params = new URLSearchParams({ page: p, per_page: 15 });
    if (filters.info !== "All") params.append("info", filters.info);
    if (filters.to) params.append("to", filters.to);
    if (filters.by !== "All") params.append("by", filters.by);
    if (filters.date_from) params.append("date_from", filters.date_from);
    if (filters.date_to) params.append("date_to", filters.date_to);
    api.get(`/history?${params.toString()}`).then((r) => setData(r.data)).catch(() => {});
  }, [filters, page]);

  useEffect(() => { load(1); setPage(1); /* eslint-disable-next-line */ }, [filters]);
  useEffect(() => { load(page); /* eslint-disable-next-line */ }, [page]);

  useEffect(() => {
    if (isTrainer) api.get("/users?role=staff").then((r) => setStaffList(r.data)).catch(() => {});
  }, [isTrainer]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (isTrainer && filters.by !== "All") setStaffLabel(filters.by);
    else setStaffLabel(user.name);
  }, [filters.by, isTrainer, user.name]);

  const pages = [];
  for (let i = 1; i <= data.total_pages; i++) pages.push(i);

  return (
    <div data-testid="history-page">
      {/* filter box */}
      <div className="filter-box">
        <div className="filter-row">
          <label>Info</label>
          <select className="form-select" value={filters.info} onChange={(e) => setF("info", e.target.value)} data-testid="filter-info">
            {INFO_OPTS.map((o) => <option key={o}>{o}</option>)}
          </select>
          <label>To</label>
          <input className="form-input" value={filters.to} onChange={(e) => setF("to", e.target.value)} data-testid="filter-to" placeholder="username" />
          <label>By</label>
          <select className="form-select" value={filters.by} onChange={(e) => setF("by", e.target.value)} data-testid="filter-by">
            <option>All</option>
            {staffList.map((s) => <option key={s.id} value={s.email.split("@")[0]}>{s.email.split("@")[0]}</option>)}
          </select>
          <button className="btn btn-gray" onClick={() => load(1)} data-testid="filter-edit">EDIT</button>
        </div>
        <div className="filter-row">
          <label>Date From</label>
          <input type="date" className="form-input" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} data-testid="filter-date-from" />
          <label>Date To</label>
          <input type="date" className="form-input" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} data-testid="filter-date-to" />
          <label>Coin</label>
          <input className="form-input" data-testid="filter-coin" placeholder="0" />
          <button className="btn btn-primary" onClick={() => load(1)} data-testid="filter-submit">Submit</button>
        </div>
      </div>

      {/* pagination top */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="pagination" data-testid="pagination">
          <span>{data.page} of {data.total_pages}</span>
          <a onClick={() => setPage(1)}>First</a>
          {pages.slice(0, 6).map((p) => (
            <a key={p} className={p === data.page ? "active" : ""} onClick={() => setPage(p)}>{p}</a>
          ))}
          <a onClick={() => setPage(Math.min(data.total_pages, page + 1))}>Next</a>
          <a onClick={() => setPage(data.total_pages)}>Last</a>
        </div>
        <button className="btn btn-gray" data-testid="export-all" onClick={() => {
          const token = localStorage.getItem("token");
          fetch(process.env.REACT_APP_BACKEND_URL + "/api/history/export", { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob()).then((b) => {
              const url = URL.createObjectURL(b);
              const a = document.createElement("a");
              a.href = url; a.download = "history_koin.csv"; a.click();
              URL.revokeObjectURL(url);
            });
        }}>Export All Data</button>
      </div>

      {/* history table */}
      <div className="history-title" data-testid="history-title">History Coin [{staffLabel}]</div>
      <table className="history-table" data-testid="history-table">
        <thead>
          <tr>
            <th style={{ width: 40 }}>No</th>
            <th style={{ width: 150 }}>Date</th>
            <th>Info</th>
            <th>to</th>
            <th>By</th>
            <th className="text-right">Coin</th>
            <th className="text-right">Last Coin</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && (
            <tr className="empty-row"><td colSpan={7}>Belum ada history</td></tr>
          )}
          {data.rows.map((r) => (
            <tr key={r.no} data-testid={`history-row-${r.no}`}>
              <td>{r.no}</td>
              <td className="nowrap">{new Date(r.date).toLocaleString("sv-SE").replace("T", " ")}</td>
              <td>{r.info}</td>
              <td><a className="link-blue">{r.to}</a></td>
              <td>{r.by}</td>
              <td className="text-right">{fmt(r.coin)}</td>
              <td className="text-right">{fmtCoin(r.last_coin)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
